// src-lit/components/product/kd-product-cart.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import {
  EVENT_NAMES,
  type PantryStateChangedDetail,
  type QuantityClampDetail,
  type VariantSelectedDetail,
} from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';
import {
  type VariantCartState,
  type VariantStateMap,
  isButtonDisabled,
  getButtonTextKey,
  formatPrice,
  computeEffectiveMax,
  computePurchasableMax,
} from './product-cart.logic';
import { calcRemainingMinimumPrice, formatPantryWeight } from '../cart/pantry.logic';

/**
 * @element kd-product-cart
 * @description バリアント選択・数量変更に応じてカートの状態を動的更新する。
 *   - 価格表示（合計 = 単価 × 数量）
 *   - 単価表示
 *   - ボタンの disabled/text
 *   - hidden input のバリアント ID
 */
@customElement('kd-product-cart')
export class KdProductCart extends LitElement {
  createRenderRoot() {
    return this;
  }

  private variantStateMap: VariantStateMap = {};

  private currentVariantId = '';

  private unsubscribeVariant: (() => void) | null = null;

  private boundOnQuantityChange = this.onQuantityChange.bind(this);

  private boundOnQuantityClamp = this.onQuantityClamp.bind(this);

  private boundOnQuantityValid = this.onQuantityValid.bind(this);

  private unsubscribePantry: (() => void) | null = null;

  private boundOnPantryClear = this.onPantryClear.bind(this);

  private pantryEls: {
    totalGeneral: HTMLElement | null;
    totalPremium: HTMLElement | null;
    count: HTMLElement | null;
    weight: HTMLElement | null;
    weightUnit: HTMLElement | null;
    remaining: HTMLElement | null;
    overstock: HTMLElement | null;
    overweight: HTMLElement | null;
    progress: HTMLElement | null;
    minBar: HTMLElement | null;
    weightBar: HTMLElement | null;
    totalMember: HTMLElement | null;
    submitCount: HTMLElement | null;
    clear: HTMLButtonElement | null;
    submit: HTMLButtonElement | null;
  } | null = null;

  private lastPantryDetail: PantryStateChangedDetail | null = null;

  private get isPantry(): boolean {
    return this.dataset.isPantry === 'true';
  }

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();

    if (this.isPantry) {
      this.unsubscribePantry = onEvent<PantryStateChangedDetail>(
        document,
        EVENT_NAMES.PANTRY.STATE_CHANGED,
        (e) => this.onPantryStateChanged(e.detail),
      );
      return;
    }

    this.loadVariantStateMap();
    this.initState();
    this.unsubscribeVariant = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
    const qtyInput = this.getQuantityInput();
    if (qtyInput) {
      qtyInput.addEventListener('change', this.boundOnQuantityChange);
    }
    this.addEventListener(
      EVENT_NAMES.PRODUCT.QUANTITY_CLAMP,
      this.boundOnQuantityClamp as EventListener,
    );
    this.addEventListener(
      EVENT_NAMES.PRODUCT.QUANTITY_VALID,
      this.boundOnQuantityValid,
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.isPantry) {
      this.unsubscribePantry?.();
      this.unsubscribePantry = null;
      this.pantryEls?.clear?.removeEventListener('click', this.boundOnPantryClear);
      this.pantryEls = null;
      this.lastPantryDetail = null;
      return;
    }

    this.unsubscribeVariant?.();
    this.unsubscribeVariant = null;
    const qtyInput = this.getQuantityInput();
    if (qtyInput) {
      qtyInput.removeEventListener('change', this.boundOnQuantityChange);
    }
    this.removeEventListener(
      EVENT_NAMES.PRODUCT.QUANTITY_CLAMP,
      this.boundOnQuantityClamp as EventListener,
    );
    this.removeEventListener(
      EVENT_NAMES.PRODUCT.QUANTITY_VALID,
      this.boundOnQuantityValid,
    );
  }

  private getQuantityInput(): HTMLInputElement | null {
    return this.querySelector<HTMLInputElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_INPUT,
    );
  }

  private loadVariantStateMap(): void {
    const script = this.querySelector<HTMLScriptElement>(
      SELECTORS.PRODUCT_CART.VARIANT_STATE_MAP,
    );
    if (!script?.textContent) return;
    try {
      this.variantStateMap = JSON.parse(script.textContent);
    } catch {
      // JSON parse 失敗時は空マップのまま
    }
  }

  /**
   * 初期バリアントの状態で UI を更新する
   */
  private initState(): void {
    const input = this.querySelector<HTMLInputElement>(
      SELECTORS.PRODUCT_CART.VARIANT_ID_INPUT,
    );
    if (!input?.value) return;

    this.currentVariantId = input.value;
    const state = this.variantStateMap[this.currentVariantId];
    if (state) {
      this.updateSubmitButton(this.currentVariantId, state.available);
      this.updateQuantityCaution(state);
      this.updatePriceDisplay();
    }
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    this.currentVariantId = detail.variantId;
    this.updateVariantInput(detail.variantId);
    this.updateSubmitButton(detail.variantId, detail.available);
    this.updateQuantityMax(detail.variantId);
    const state = this.variantStateMap[detail.variantId];
    if (state) this.updateQuantityCaution(state);
    this.updatePriceDisplay();
  }

  private onQuantityChange(): void {
    // 会員別価格・単価は数量非依存（数量=1 想定）なので、数量変更では再描画不要。
    this.updateQuantityCautionVisibility();
  }

  private onQuantityClamp(event: CustomEvent<QuantityClampDetail>): void {
    if (event.detail.reason !== 'max-exceeded') return;
    this.showQuantityError();
  }

  private onQuantityValid(): void {
    this.hideQuantityError();
    this.updateQuantityCautionVisibility();
  }

  /**
   * 数量エラー（在庫または購入上限超過）を表示する。
   * メッセージの max 値はバリアント切替時に Liquid テンプレートと updateQuantityCaution で同期する。
   */
  private showQuantityError(): void {
    const container = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_ERROR,
    );
    if (container) container.hidden = false;
  }

  private hideQuantityError(): void {
    const container = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_ERROR,
    );
    if (container) container.hidden = true;
  }

  /**
   * 注意文言とエラー文言の max / 現在数の値（テキスト）をバリアントに合わせて差し替える。
   * 表示・非表示は updateQuantityCautionVisibility で別途判定する。
   * 在庫管理なし / max <= 0 のバリアントでは注意もエラーも意味がないので両方非表示にして終了。
   */
  private updateQuantityCaution(state: VariantCartState): void {
    const caution = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_CAUTION,
    );
    const effectiveMax = computeEffectiveMax(state);

    if (effectiveMax == null || effectiveMax <= 0) {
      if (caution) caution.hidden = true;
      this.hideQuantityError();
      return;
    }

    const maxEls = this.querySelectorAll<HTMLElement>(
      `${SELECTORS.PRODUCT_CART.QUANTITY_CAUTION_MAX}, ${SELECTORS.PRODUCT_CART.QUANTITY_ERROR_MAX}`,
    );
    maxEls.forEach((el) => {
      el.textContent = String(effectiveMax);
    });
    const currentEl = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_CAUTION_CURRENT,
    );
    if (currentEl) currentEl.textContent = String(state.inCartQuantity);
    this.updateQuantityCautionVisibility();
  }

  /**
   * 注意文言の表示判定。
   * 入力値が input.max（= 購入可能残数）に到達 / 超過しているときのみ表示する。
   * - 値 < max: 非表示
   * - 値 = max: 表示（マックスに到達）
   * - 値 > max: 表示（エラー文言と同時に表示。clamp で直後 value=max になる）
   * 在庫管理なし / max 未指定 / state なしのバリアントでは常に非表示。
   */
  private updateQuantityCautionVisibility(): void {
    const caution = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_CAUTION,
    );
    if (!caution) return;
    const state = this.variantStateMap[this.currentVariantId];
    if (!state || computeEffectiveMax(state) == null) {
      caution.hidden = true;
      return;
    }
    const input = this.getQuantityInput();
    if (!input || !input.max) {
      caution.hidden = true;
      return;
    }
    const value = Number(input.value);
    const max = Number(input.max);
    const reached =
      Number.isFinite(value) && Number.isFinite(max) && value >= max;
    caution.hidden = !reached;
  }

  /**
   * hidden input のバリアント ID を更新する
   */
  private updateVariantInput(variantId: string): void {
    const input = this.querySelector<HTMLInputElement>(
      SELECTORS.PRODUCT_CART.VARIANT_ID_INPUT,
    );
    if (input) {
      input.value = variantId;
    }
  }

  /**
   * data-cart-* 要素の走査ルート。
   * SP 固定フッターなど kd-product-cart の外にある要素も含めるため、
   * 配下の Shopify セクションをスコープにする。同ページに複数 kd-product-cart
   * （v2_product_main / v2_product_content）があってもセクション境界で分離される。
   */
  private getQueryRoot(): ParentNode {
    return this.closest('section') ?? document;
  }

  /**
   * カート追加ボタンの disabled/text を更新する。
   * 同一セクション内の data-cart-submit を全て同期する（メインカート + SP 固定フッター）。
   */
  private updateSubmitButton(
    variantId: string,
    available: boolean,
  ): void {
    const buttons = this.getQueryRoot().querySelectorAll<HTMLButtonElement>(
      SELECTORS.PRODUCT_CART.SUBMIT,
    );
    if (buttons.length === 0) return;

    const state = this.variantStateMap[variantId];
    const disabled = state ? isButtonDisabled(state) : !available;
    const textKey = getButtonTextKey(disabled);
    const text = window.variantStrings?.[textKey] ?? '';

    buttons.forEach((button) => {
      if (disabled) {
        button.setAttribute('disabled', 'disabled');
        // 売り切れが確定したら kd-cart-add-form の起動時に有効化させない
        button.removeAttribute('data-cart-submit-pending');
      } else if (!button.matches(SELECTORS.PRODUCT_CART.SUBMIT_PENDING)) {
        // pending 中は kd-cart-add-form が submit を購読するまで disabled を維持する
        button.removeAttribute('disabled');
      }
      if (text) {
        const label = button.querySelector<HTMLElement>(
          SELECTORS.PRODUCT_CART.SUBMIT_LABEL,
        );
        if (label) label.textContent = text;
      }
    });
  }

  /**
   * バリアント切替時に数量入力の max を「購入可能残数」（effective max - inCart）で更新する。
   * カートに既に max 個入っているケースは max=1 まで下げ、ユーザー操作でエラー表示を出せるようにする。
   */
  private updateQuantityMax(variantId: string): void {
    const state = this.variantStateMap[variantId];
    const input = this.getQuantityInput();
    if (!state || !input) return;

    const purchasableMax = computePurchasableMax(state);
    if (purchasableMax == null) {
      // 在庫追跡なし（ギフトカード等）は max 制限しない
      input.removeAttribute('max');
      return;
    }
    const maxAttr = Math.max(1, purchasableMax);
    input.max = String(maxAttr);

    if (Number(input.value) > maxAttr) {
      input.value = String(maxAttr);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * バリアント選択に応じて SP 追従バーの価格・単価を更新する。
   * 買い物ボックス／SP 追従バーの会員別 2 価格併記は Phase6 で撤去し、主価格は上部の
   * kd-product-pricing に集約したため、ここで更新するのは追従バー（updateFooter）のみ。
   */
  private updatePriceDisplay(): void {
    const state = this.variantStateMap[this.currentVariantId];
    if (!state) return;

    this.updateFooter(state);
  }

  /**
   * SP 追従バーの単一価格（会員種別 tier）・ラベル・単価を更新する（mock 20260707）。
   * tier は追従バーの data-member-tier で SSR 確定。プレミアムでも当該 variant に
   * premium 価格が無ければ一般価格へフォールバックし、ラベル/色も一般表示へ戻す
   * （v2-product-pricing の主価格スイッチと同判定）。
   */
  private updateFooter(state: VariantCartState): void {
    const footer = this.getQueryRoot().querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.FOOTER,
    );
    if (!footer) return;

    const tier = footer.dataset.memberTier ?? 'general';
    const showPremium = tier === 'premium' && state.pricePremium !== null;
    const mainPrice =
      tier === 'premium' && state.pricePremium !== null
        ? state.pricePremium
        : state.priceGeneral;

    const priceEl = footer.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.FOOTER_PRICE,
    );
    if (priceEl) priceEl.textContent = formatPrice(mainPrice);

    // プレミアム tier では variant により premium↔一般 が切り替わるためラベル/色も追従。
    if (tier === 'premium') {
      const labelEl = footer.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_CART.FOOTER_LABEL,
      );
      if (labelEl) {
        labelEl.textContent = showPremium ? 'プレミアム会員価格' : '一般会員価格';
      }
      const wrapEl = footer.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_CART.FOOTER_PRICE_WRAP,
      );
      if (wrapEl) {
        wrapEl.classList.toggle('tw:text-primary2', showPremium);
        wrapEl.classList.toggle('tw:text-text-main', !showPremium);
      }
    }

    const unitEl = footer.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_CART.FOOTER_UNIT,
    );
    if (unitEl) {
      if (state.unitPrice != null && state.unitName) {
        unitEl.hidden = false;
        const unitLabelEl = footer.querySelector<HTMLElement>(
          SELECTORS.PRODUCT_CART.FOOTER_UNIT_LABEL,
        );
        if (unitLabelEl) unitLabelEl.textContent = `1${state.unitName}`;
        const unitPriceEl = footer.querySelector<HTMLElement>(
          SELECTORS.PRODUCT_CART.FOOTER_UNIT_PRICE,
        );
        if (unitPriceEl) unitPriceEl.textContent = formatPrice(state.unitPrice);
      } else {
        unitEl.hidden = true;
      }
    }
  }

  /**
   * フッターの「クリア」押下。kd-pantry-products は本要素の外側にあるため
   * document 経由でクリアを要求する（受け側が全カードの数量を 0 に戻す）。
   */
  private onPantryClear(): void {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAMES.PANTRY.CLEAR_REQUESTED, {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private cachePantryEls(): void {
    if (this.pantryEls) return;
    this.pantryEls = {
      totalGeneral: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.TOTAL_GENERAL),
      totalPremium: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.TOTAL_PREMIUM),
      count: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.COUNT),
      weight: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.WEIGHT),
      weightUnit: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.WEIGHT_UNIT),
      remaining: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.REMAINING),
      overstock: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.OVERSTOCK),
      overweight: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.OVERWEIGHT),
      progress: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.PROGRESS),
      minBar: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.MIN_BAR),
      weightBar: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.WEIGHT_BAR),
      totalMember: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.TOTAL_MEMBER),
      submitCount: this.querySelector<HTMLElement>(SELECTORS.PANTRY_CART.SUBMIT_COUNT),
      clear: this.querySelector<HTMLButtonElement>(SELECTORS.PANTRY_CART.CLEAR),
      submit: this.querySelector<HTMLButtonElement>(SELECTORS.PANTRY_CART.SUBMIT),
    };

    // 「クリア」→ kd-pantry-products（本要素の外側）へ document 経由で通知する。
    this.pantryEls.clear?.addEventListener('click', this.boundOnPantryClear);
  }

  private onPantryStateChanged(detail: PantryStateChangedDetail): void {
    const prev = this.lastPantryDetail;
    if (
      prev &&
      prev.total === detail.total &&
      prev.count === detail.count &&
      prev.weightG === detail.weightG &&
      prev.hasOverStock === detail.hasOverStock &&
      prev.hasMaxExceeded === detail.hasMaxExceeded &&
      prev.hasOverWeight === detail.hasOverWeight &&
      prev.minimumPrice === detail.minimumPrice
    ) {
      return;
    }
    this.lastPantryDetail = detail;

    this.cachePantryEls();
    const els = this.pantryEls;
    if (!els) return;

    const {
      total,
      totalGeneral,
      totalPremium,
      count,
      weightG,
      hasOverStock,
      hasMaxExceeded,
      hasOverWeight,
      minimumPrice,
    } = detail;
    if (els.totalGeneral) els.totalGeneral.textContent = totalGeneral.toLocaleString('ja-JP');
    if (els.totalPremium) els.totalPremium.textContent = totalPremium.toLocaleString('ja-JP');
    // mock 準拠の単一表示: 閲覧者の会員種別に応じた送料込み合計を出す。
    // 会員種別は SSR が data-member-tier（general / premium）に焼き込む。
    if (els.totalMember) {
      const isPremium = this.dataset.memberTier === 'premium';
      const memberTotal = isPremium ? totalPremium : totalGeneral;
      els.totalMember.textContent = memberTotal.toLocaleString('ja-JP');
    }
    if (els.count) els.count.textContent = String(count);
    if (els.submitCount) els.submitCount.textContent = String(count);
    if (els.weight) {
      const formatted = formatPantryWeight(weightG);
      els.weight.textContent = formatted.value;
      if (els.weightUnit) els.weightUnit.textContent = formatted.unit;
    }
    if (els.remaining) {
      const remaining = calcRemainingMinimumPrice(minimumPrice, total);
      els.remaining.textContent = remaining.toLocaleString('ja-JP');
    }
    if (els.overstock) els.overstock.hidden = !hasOverStock;
    if (els.overweight) els.overweight.hidden = !hasOverWeight;

    // 走行合計の進捗バー（最低金額 / 最大重量）。width = 割合を 100% でクランプ。
    // 最大重量超過時は重量バーを赤（status-sale）、通常は緑（eco）にする。
    if (els.progress) {
      const min = Number(els.progress.dataset.pantryMin) || 0;
      const maxWeightG = Number(els.progress.dataset.pantryMaxWeightG) || 0;
      if (els.minBar) {
        let pct = 0;
        if (min > 0) pct = Math.min((total / min) * 100, 100);
        else if (total > 0) pct = 100;
        els.minBar.style.width = `${pct}%`;
      }
      if (els.weightBar) {
        const pct = maxWeightG > 0 ? Math.min((weightG / maxWeightG) * 100, 100) : 0;
        els.weightBar.style.width = `${pct}%`;
        els.weightBar.classList.toggle('tw:bg-status-sale', hasOverWeight);
        els.weightBar.classList.toggle('tw:bg-eco', !hasOverWeight);
      }
    }
    if (els.submit) {
      // hasMaxExceeded はカード単位の最大購入数超過。手入力で max を超えた値を
      // 入れたケース（pantry-products.logic.ts#calcPantryTotals 参照）でも submit
      // をブロックする必要があるため、disabled 条件に含める。
      // hasOverWeight は 1 注文最大重量の超過（issue #3179）。
      els.submit.disabled =
        total < minimumPrice ||
        hasOverStock ||
        hasMaxExceeded ||
        hasOverWeight ||
        count <= 0;
    }
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-cart': KdProductCart;
  }
}
